import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import {
  useSendConnectionRequestMutation,
  useGetAllConnectedQuery,
  useAcceptConnectionRequestMutation,
} from "../features/connection/connectionApiSlice";
import { apiSlice } from "../features/api/apiSlice";

import { useLocation } from "react-router-dom";

const normalizeBT = (bt, profile) => {
  if (profile?.generalPublic === true && !bt) return "generalPublic";
  const raw = (bt || "").toString().trim();
  if (!raw) return "generalPublic";
  const compact = raw.toLowerCase().replace(/[\s_-]+/g, "");
  if (compact === "generalpublic") return "generalPublic";
  return raw;
};

const idStr = (v) => {
  if (v == null || v === "" || v === "null" || v === "undefined") return null;
  if (typeof v === "object") return String(v._id || v.id || "");
  const s = String(v);
  return s && s !== "null" && s !== "undefined" ? s : null;
};

const isBlankShop = (s) => !idStr(s);

export const usePostState = ({
  data,
  id,
  mybusinessType,
  setShow,
  mysetting,
}) => {
  const { name } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track locally sent requests (for instant UI feedback before refetch)
  const [localPendingIds, setLocalPendingIds] = useState(new Set());
  // Track locally accepted requests
  const [localAcceptedIds, setLocalAcceptedIds] = useState(new Set());

  const [sendConnectionRequest, { isLoading: isSending }] =
    useSendConnectionRequestMutation();
  const [acceptConnectionRequest, { isLoading: isAccepting }] =
    useAcceptConnectionRequestMutation();

  // Scope strictly by page — never by mybusinessType alone.
  // /user-profile connections must land on personal Connectivity;
  // /marketing-profile connections must land on marketing Connectivity.
  const path = location.pathname || "";
  const isOnMarketingPage =
    path === "/marketing-profile" || path.endsWith("/marketing-profile");
  const isOnUserProfilePage =
    path === "/user-profile" || path.endsWith("/user-profile");
  const isMarketerProfile = isOnMarketingPage;

  const resolveSenderStamp = () => {
    if (isOnMarketingPage) {
      return {
        senderBusinessType: "marketerProfile",
        asMarketerProfile: true,
      };
    }
    if (isOnUserProfilePage) {
      return {
        senderBusinessType: "generalPublic",
        asMarketerProfile: false,
      };
    }
    // Shop / other pages: keep the active shop/profile BT
    return {
      senderBusinessType: mybusinessType || "generalPublic",
      asMarketerProfile: mybusinessType === "marketerProfile",
    };
  };

  // Fetch existing connection data (requested / pending / connected)
  // Personal profile: aggregate by userid only (all shops + general).
  const connectionQueryParams = useMemo(() => {
    if (id) return { shopid: id, userid: name?._id };
    return {
      userid: name?._id,
      ...(isMarketerProfile ? { isMarketerProfile: true } : {}),
    };
  }, [id, name?._id, isMarketerProfile]);

  const { data: connectionData, isLoading: connectionLoading } =
    useGetAllConnectedQuery(connectionQueryParams, {
      skip: !name?._id,
      refetchOnMountOrArgChange: true,
    });

  const myUserId = idStr(name?._id);
  const myShopId = idStr(id);
  const myBT = normalizeBT(mybusinessType);

  /**
   * Status keys for the OTHER party.
   * - Shop targets: shopId + BT (and bare shopId — shops are unique).
   * - User targets: userId + BT; bare userId ONLY for generalPublic so a GP
   *   request does not mark that person's shop profiles as Pending.
   */
  const addOtherPartyKeys = (ids, { shopId, userId, bt }) => {
    const nbt = normalizeBT(bt);
    const compact = (bt || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const sid = idStr(shopId);
    const uid = idStr(userId);
    if (sid) {
      ids.add(`${sid}-${nbt}`);
      ids.add(`${sid}-${compact}`);
      ids.add(sid);
    }
    if (uid) {
      ids.add(`${uid}-${nbt}`);
      ids.add(`${uid}-${compact}`);
      if (!sid && (nbt === "generalPublic" || compact === "generalpublic")) {
        ids.add(uid);
      }
    }
  };

  // Only rows that belong to the profile we're currently viewing
  // (GP timeline ≠ shop timeline — each has its own sent/pending/connected).
  const isMyOutgoingRow = (row) => {
    const senderShopId = idStr(row?.sendershopid);
    const senderCtx = idStr(row?.senderShopContextId);
    const senderBT = normalizeBT(row?.senderBusinessType);
    if (!myShopId) {
      if (isMarketerProfile) {
        if (idStr(row?.senderid) !== myUserId) return false;
        if (!isBlankShop(senderShopId)) return false;
        const tabBT = normalizeBT(mysetting);
        const rowBT = normalizeBT(row?.businessType);
        if (mysetting === "home") return true;
        if (tabBT === "generalPublic") return rowBT === "generalPublic";
        if (tabBT === "marketerProfile") return rowBT === "marketerProfile";
        return rowBT === tabBT;
      }
      return (
        isBlankShop(senderShopId) &&
        (senderBT === "generalPublic" ||
          senderBT === myBT ||
          !row?.senderBusinessType)
      );
    }
    // Shop timeline: match shop id OR context id (shop→GP often only has context)
    if (senderShopId === myShopId || senderCtx === myShopId) return true;
    return (
      !senderShopId &&
      idStr(row?.senderid) === myUserId &&
      (senderBT === myBT || normalizeBT(row?.businessType) === "generalPublic")
    );
  };

  const isMyIncomingRow = (row) => {
    const receiverShopId = idStr(row?.recievershopid);
    const receiverCtx = idStr(row?.recieverShopContextId);
    const receiverBT = normalizeBT(row?.businessType);
    if (!myShopId) {
      if (isMarketerProfile) {
        if (idStr(row?.recieverid) !== myUserId) return false;
        const tabBT = normalizeBT(mysetting);
        const rowBT = normalizeBT(row?.businessType);
        const senderBT = normalizeBT(row?.senderBusinessType);
        if (mysetting === "home") return true;
        if (tabBT === "generalPublic") {
          return (
            rowBT === "generalPublic" ||
            (isBlankShop(receiverShopId) && isBlankShop(row?.sendershopid))
          );
        }
        if (tabBT === "marketerProfile")
          return rowBT === "marketerProfile" || senderBT === "marketerProfile";
        return rowBT === tabBT || senderBT === tabBT;
      }
      return (
        isBlankShop(receiverShopId) &&
        (receiverBT === "generalPublic" ||
          receiverBT === myBT ||
          !row?.businessType)
      );
    }
    if (receiverShopId === myShopId || receiverCtx === myShopId) return true;
    return (
      !receiverShopId &&
      idStr(row?.recieverid) === myUserId &&
      (receiverBT === myBT ||
        normalizeBT(row?.senderBusinessType) === "generalPublic")
    );
  };

  // Fully connected parties (checked first — never show Pending if connected).
  // Also mirrors connectedBT (same source as Profile Connectivity boxes).
  const connectedIds = useMemo(() => {
    const ids = new Set();
    const connected = connectionData?.connected || [];

    connected.forEach((conn) => {
      const senderId = idStr(conn.senderid);
      const senderShopId = idStr(conn.sendershopid);
      const receiverId = idStr(conn.recieverid);
      const receiverShopId = idStr(conn.recievershopid);
      const bt = conn.businessType;
      const senderBT = conn.senderBusinessType;

      const isSender = myUserId && senderId === myUserId;
      const isReceiver = myUserId && receiverId === myUserId;

      if (isSender) {
        addOtherPartyKeys(ids, {
          shopId: receiverShopId,
          userId: receiverId,
          bt,
        });
        if (receiverId && normalizeBT(bt) === "generalPublic") {
          ids.add(receiverId);
          ids.add(`${receiverId}-generalPublic`);
        }
      }
      if (isReceiver) {
        const effectiveBT = senderBT || bt;
        addOtherPartyKeys(ids, {
          shopId: senderShopId,
          userId: senderId,
          bt: effectiveBT,
        });
        if (senderId && normalizeBT(effectiveBT) === "generalPublic") {
          ids.add(senderId);
          ids.add(`${senderId}-generalPublic`);
        }
      }
    });

    // Connectivity boxes — include all groups so connected users are hidden from right-side suggestion tabs only for their specific business type
    (connectionData?.connectedBT || []).forEach((group) => {
      const bt = group?.businessType;
      const nbt = normalizeBT(bt);
      (group?.items || []).forEach((item) => {
        addOtherPartyKeys(ids, {
          shopId: item?.shopid,
          userId: item?.userid,
          bt,
        });
        const uid = idStr(item?.userid);
        if (uid && nbt === "generalPublic") {
          ids.add(uid);
          ids.add(`${uid}-generalPublic`);
        }
      });
    });

    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionData, myUserId, myShopId, myBT, isMarketerProfile]);

  // Outgoing requests we sent from THIS profile (Pending)
  const requestedIds = useMemo(() => {
    const ids = new Set();
    const requested = connectionData?.requested || [];
    requested.forEach((req) => {
      if (!isMyOutgoingRow(req)) return;

      const bt = req.businessType;
      const receiverShopId = idStr(req.recievershopid);
      const receiverId = idStr(req.recieverid);
      const shopKey = receiverShopId
        ? `${receiverShopId}-${normalizeBT(bt)}`
        : null;
      const userKey = receiverId ? `${receiverId}-${normalizeBT(bt)}` : null;
      if (
        (shopKey && connectedIds.has(shopKey)) ||
        (userKey && connectedIds.has(userKey)) ||
        (receiverShopId && connectedIds.has(receiverShopId)) ||
        (!receiverShopId &&
          receiverId &&
          normalizeBT(bt) === "generalPublic" &&
          connectedIds.has(receiverId))
      ) {
        return;
      }
      addOtherPartyKeys(ids, {
        shopId: receiverShopId,
        userId: receiverId,
        bt,
      });
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionData, connectedIds, myShopId, myBT]);

  // Incoming requests to THIS profile (Accept)
  const pendingIds = useMemo(() => {
    const ids = new Set();
    const pending = connectionData?.pending || [];
    pending.forEach((req) => {
      if (!isMyIncomingRow(req)) return;

      const bt = req.senderBusinessType;
      const senderShopId = idStr(req.sendershopid);
      const senderId = idStr(req.senderid);
      if (
        (senderShopId &&
          (connectedIds.has(`${senderShopId}-${normalizeBT(bt)}`) ||
            connectedIds.has(senderShopId))) ||
        (senderId &&
          (connectedIds.has(`${senderId}-${normalizeBT(bt)}`) ||
            (!senderShopId &&
              normalizeBT(bt) === "generalPublic" &&
              connectedIds.has(senderId))))
      ) {
        return;
      }
      addOtherPartyKeys(ids, {
        shopId: senderShopId,
        userId: senderId,
        bt,
      });
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionData, connectedIds, myShopId, myBT]);

  // Keys for a right-side card — scoped so GP ≠ shop of the same owner
  const profileKeys = (profile) => {
    const profileId = idStr(profile?._id);
    const profileUserId = idStr(profile?.userid) || profileId;
    const profileBT = normalizeBT(profile?.businessType, profile);
    const compact = (profile?.businessType || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    const isGP =
      profileBT === "generalPublic" ||
      compact === "generalpublic" ||
      profile?.generalPublic === true;
    const keys = [];

    if (isGP) {
      const uid = profileUserId || profileId;
      if (uid) {
        keys.push(`${uid}-generalPublic`, `${uid}-generalpublic`, uid);
      }
    } else if (profileId) {
      // Shop card: never include owner userid bare — that leaked GP Pending
      // onto every shop tab for the same person.
      keys.push(
        `${profileId}-${profileBT}`,
        `${profileId}-${compact}`,
        profileId,
      );
    }
    return keys;
  };

  // Returns the connection status for a given profile
  const getConnectionStatus = (profile) => {
    if (connectionLoading) return "loading";

    const keys = profileKeys(profile);
    if (!keys.length) return "connect";

    if (keys.some((k) => localAcceptedIds.has(k) || connectedIds.has(k))) {
      return "connected";
    }
    if (keys.some((k) => pendingIds.has(k))) return "accept";
    if (keys.some((k) => localPendingIds.has(k) || requestedIds.has(k))) {
      return "pending";
    }
    return "connect";
  };

  // Hide from this tab when already connected (Connectivity box keeps them).
  const isConnectedForCurrentTab = (profile) => {
    const keys = profileKeys(profile);
    return keys.some((k) => connectedIds.has(k) || localAcceptedIds.has(k));
  };

  // Filter / search effect
  useEffect(() => {
    if (!data || !Array.isArray(data)) {
      setDisplayData([]);
      setLoading(true);
      return;
    }

    setLoading(false);
    const query = searchTerm.trim().toLowerCase();

    // Personal / marketing suggestion strips: hide already-connected (they live
    // on left Connectivity). Shop timelines: KEEP connected people visible on
    // the matching tab (e.g. generalPublic) with a ✓ Connected button — otherwise
    // accepted GP→shop connects never appear on the right-side GP tab.
    const filteredByConnection = data.filter((profile) => {
      const isConn =
        profile?._fromConnected === true ||
        isConnectedForCurrentTab(profile) ||
        getConnectionStatus(profile) === "connected";
      if (isConn) {
        if (myShopId || profile?._fromConnected === true) return true;
        return false;
      }
      return true;
    });

    if (query === "") {
      setDisplayData(filteredByConnection);
    } else {
      const filtered = filteredByConnection.filter((profile) => {
        const nameToSearch = (
          (profile?.shopname || "") +
          (profile?.profileName || "") +
          (profile?.companyname || "") +
          (profile?.fullname || "")
        ).toLowerCase();
        return nameToSearch.includes(query);
      });
      setDisplayData(filtered);
    }
  }, [
    data,
    searchTerm,
    connectedIds,
    localAcceptedIds,
    requestedIds,
    pendingIds,
    myShopId,
  ]);

  // Drop optimistic pending once server says connected
  useEffect(() => {
    if (!localPendingIds.size || !connectedIds.size) return;
    setLocalPendingIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      prev.forEach((k) => {
        if (connectedIds.has(k)) {
          next.delete(k);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [connectedIds, localPendingIds.size]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleClear = () => {
    setSearchTerm("");
  };

  const ConnectShop = async (profile) => {
    const keys = profileKeys(profile);

    // Prevent duplicate requests
    if (getConnectionStatus(profile) === "pending") return;
    if (getConnectionStatus(profile) === "connected") return;

    let requestBody = {};
    const { senderBusinessType: stampSenderBT, asMarketerProfile: stampAsMarketer } =
      resolveSenderStamp();
    // Target type must come from the card / active shop tab — NOT from
    // mysetting alone. Using `mysetting === "generalPublic"` treated shop
    // cards as people whenever the GP tab was active, so the request was
    // stored without recievershopid and never landed on Retail/Services/…
    // Profile Connectivity cards on /user-profile.
    // Exception: the General Public tab itself always lists people (User
    // docs). Those must stay GP even if the user doc has a leftover shop BT,
    // otherwise zone checks wrongly block out-of-zone GP connects.
    const onGeneralPublicTab = mysetting === "generalPublic";
    const tabBT =
      mysetting &&
      mysetting !== "home" &&
      mysetting !== "generalPublic" &&
      mysetting !== "marketerProfile"
        ? mysetting
        : null;
    const targetBT = onGeneralPublicTab
      ? "generalPublic"
      : normalizeBT(profile?.businessType || tabBT || "", profile);
    const targetIsPerson =
      onGeneralPublicTab ||
      !targetBT ||
      targetBT === "generalPublic" ||
      targetBT === "marketerProfile" ||
      profile?.generalPublic === true;
    // Keep marketer targets stamped as marketerProfile (not forced GP)
    // so receiver-side marketing Connectivity can see them.
    const personReceiverBT =
      onGeneralPublicTab
        ? "generalPublic"
        : targetBT === "marketerProfile"
          ? "marketerProfile"
          : "generalPublic";

    if (
      targetIsPerson &&
      (stampSenderBT === "generalPublic" ||
        stampSenderBT === "marketerProfile")
    ) {
      requestBody = {
        senderid: name._id,
        senderBusinessType: stampSenderBT,
        businessType: personReceiverBT,
        recieverid: profile?.userid ? profile?.userid : profile?._id,
        asMarketerProfile: !!stampAsMarketer,
      };
    } else if (targetIsPerson) {
      // From a shop timeline → person (GP): stamp sendershopid so the row
      // lands on this shop's Connectivity. Zone checks skip GP targets, so
      // sendershopid is safe here. Also keep senderShopContextId as backup.
      const fromShopTimeline =
        Boolean(id) &&
        !stampAsMarketer &&
        stampSenderBT &&
        stampSenderBT !== "generalPublic" &&
        stampSenderBT !== "marketerProfile";
      requestBody = {
        senderid: name._id,
        senderBusinessType: stampSenderBT,
        businessType: personReceiverBT,
        recieverid: profile?.userid ? profile?.userid : profile?._id,
        ...(fromShopTimeline
          ? { sendershopid: id, senderShopContextId: id }
          : id && !stampAsMarketer
            ? { senderShopContextId: id }
            : {}),
        asMarketerProfile: !!stampAsMarketer,
      };
    } else {
      // Shop / industry target — keep shop id for Connectivity cards
      const receiverUserId =
        profile?.userid?._id ||
        profile?.userid ||
        (profile?.userid ? profile.userid : null);
      const receiverShopId =
        profile?.shopid?._id ||
        profile?.shopid ||
        profile?._id;
      requestBody = {
        senderid: name._id,
        ...(!stampAsMarketer && id ? { sendershopid: id } : {}),
        senderBusinessType: stampSenderBT,
        // Prefer explicit shop BT; fall back to the active suggestion tab BT
        // so /user-profile Connect always stamps the shop type for Connectivity.
        businessType: profile?.businessType || tabBT,
        recieverid: receiverUserId || profile?._id,
        recievershopid: receiverShopId,
        asMarketerProfile: !!stampAsMarketer,
      };
    }

    try {
      // Optimistically mark as pending for instant UI feedback
      setLocalPendingIds((prev) => new Set([...prev, ...keys]));

      await sendConnectionRequest(requestBody).unwrap();
      SuccessNotify("Connection request sent successfully!");
    } catch (error) {
      // Revert optimistic update on failure
      setLocalPendingIds((prev) => {
        const updated = new Set(prev);
        keys.forEach((k) => updated.delete(k));
        return updated;
      });
      FailedNotify(
        error?.data?.message || "Failed to send connection request.",
      );
    }
  };

  const handleAccept = async (profile) => {
    const keys = profileKeys(profile);

    let requestBody = {};
    const receiverId = profile?.userid ? profile?.userid : profile?._id;

    const findMatchingPendingRow = () => {
      const pending = connectionData?.pending || [];
      const otherUserId = idStr(receiverId);
      const profileShopId =
        profile?.businessType &&
        profile?.businessType !== "generalPublic" &&
        !profile?.generalPublic
          ? idStr(profile?._id)
          : null;
      for (const req of pending) {
        if (idStr(req.recieverid) !== myUserId) continue;
        const senderMatch =
          otherUserId && idStr(req.senderid) === otherUserId;
        const shopSenderMatch =
          profileShopId && idStr(req.sendershopid) === profileShopId;
        if (!senderMatch && !shopSenderMatch) continue;
        if (isMarketerProfile && mysetting !== "home") {
          const tabBT = normalizeBT(mysetting);
          const rowBT = normalizeBT(req.businessType);
          const sendBT = normalizeBT(req.senderBusinessType);
          if (tabBT === "marketerProfile") continue;
          if (
            tabBT !== "generalPublic" &&
            rowBT !== tabBT &&
            sendBT !== tabBT
          ) {
            continue;
          }
        }
        return req;
      }
      return null;
    };

    const pendingRow = findMatchingPendingRow();
    if (pendingRow) {
      const pendingSenderShop =
        idStr(pendingRow.sendershopid) ||
        // Recover shop id when shop→GP only stored context / blank sendershopid
        (myShopId &&
        !isMarketerProfile &&
        normalizeBT(pendingRow.senderBusinessType) === myBT
          ? myShopId
          : null);
      const pendingReceiverShop =
        idStr(pendingRow.recievershopid) ||
        (myShopId &&
        !isMarketerProfile &&
        normalizeBT(pendingRow.businessType) === myBT
          ? myShopId
          : null);
      requestBody = {
        senderid: pendingRow.senderid,
        recieverid: pendingRow.recieverid,
        sendershopid: pendingSenderShop,
        recievershopid: pendingReceiverShop,
        senderBusinessType: pendingRow.senderBusinessType,
        businessType: pendingRow.businessType,
        // Carry the originating shop of a generalPublic request through the
        // accept so the connection stays on that shop's Connectivity box.
        ...(idStr(pendingRow.senderShopContextId) || pendingSenderShop
          ? {
              senderShopContextId:
                idStr(pendingRow.senderShopContextId) || pendingSenderShop,
            }
          : {}),
        ...(idStr(pendingRow.recieverShopContextId) ||
        pendingReceiverShop ||
        (myShopId && !isMarketerProfile)
          ? {
              recieverShopContextId:
                idStr(pendingRow.recieverShopContextId) ||
                pendingReceiverShop ||
                myShopId,
            }
          : {}),
        ...(pendingRow.asMarketerProfile === true ||
        pendingRow.fromMarketerProfile === true ||
        isMarketerProfile
          ? { asMarketerProfile: true }
          : {}),
      };
    } else if (
      (profile?.businessType === "generalPublic" ||
        profile?.generalPublic === true) &&
      !isMarketerProfile &&
      (mybusinessType === "generalPublic" || isOnUserProfilePage)
    ) {
      requestBody = {
        senderid: profile?._id,
        senderBusinessType: "generalPublic",
        recieverid: name._id,
        businessType: "generalPublic",
      };
    } else if (
      profile?.businessType === "generalPublic" ||
      profile?.generalPublic === true
    ) {
      requestBody = {
        senderid: receiverId,
        senderBusinessType: "generalPublic",
        recieverid: name._id,
        ...(id && !isMarketerProfile ? { recievershopid: id } : {}),
        businessType: isMarketerProfile ? "marketerProfile" : mybusinessType,
        ...(isMarketerProfile ? { asMarketerProfile: true } : {}),
      };
    } else {
      requestBody = {
        senderid: receiverId,
        sendershopid: profile?._id,
        senderBusinessType: profile?.businessType,
        recieverid: name._id,
        ...(id && !isMarketerProfile ? { recievershopid: id } : {}),
        businessType: isMarketerProfile ? "marketerProfile" : mybusinessType,
        ...(isMarketerProfile ? { asMarketerProfile: true } : {}),
      };
    }

    try {
      setLocalAcceptedIds((prev) => new Set([...prev, ...keys]));
      const res = await acceptConnectionRequest(requestBody).unwrap();
      dispatch(
        apiSlice.util.invalidateTags(["UserConnections", "Connections"]),
      );
      SuccessNotify(res?.message || "Connection request accepted!");
    } catch (error) {
      setLocalAcceptedIds((prev) => {
        const updated = new Set(prev);
        keys.forEach((k) => updated.delete(k));
        return updated;
      });
      FailedNotify(
        error?.data?.message || "Failed to accept connection request",
      );
    }
  };

  const handleClose = () => setShow(0);
  const handleOpenModal = () => setShow(true);

  return {
    searchTerm,
    setSearchTerm,
    displayData,
    loading,
    isSending,
    isAccepting,
    handleSearch,
    handleClear,
    ConnectShop,
    getConnectionStatus,
    handleAccept,
    handleClose,
    handleOpenModal,
  };
};
